import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconCheck, IconX } from '@tabler/icons-react';
import Hint from '../../../components/common/Hint';
import { useTranslation } from '../../../i18n/hooks/useTranslation';

/**
 * Small chrome primitives for the editor. Deliberately local to the feature:
 * the site's shared UI kit is built for form pages, while these need to be
 * dense, dark-mode aware and safe to stack above a full-screen overlay.
 */

export const EDITOR_Z = 9000;

/* ----------------------------------------------------------------- focus */

/**
 * Editing text and using the toolbar at the same time.
 *
 * The inline editor is a real `<textarea>`, so every click on the options bar
 * blurs it — and its blur handler commits the edit (and throws away a box that
 * is still empty, which took the text row of the toolbar down with it). Chrome
 * that must NOT end an edit carries `data-pdf-editor-chrome`; the textarea
 * checks for it before committing, and hands the caret back afterwards.
 */
export const EDITOR_CHROME_ATTR = 'data-pdf-editor-chrome';
export const TEXT_EDITOR_ATTR = 'data-pdf-text-editor';

/** Whether focus is moving to (or the click landed on) editor chrome. */
export const isEditorChrome = (node) => !!node?.closest?.(`[${EDITOR_CHROME_ATTR}]`);

/** Put the caret back in the open text box, if one is still open. */
export function refocusTextEditor() {
    const find = () => document.querySelector(`[${TEXT_EDITOR_ATTR}]`);
    // Now, so a keystroke right behind the click still lands in the box, and
    // again next frame, because the click that stole the caret may still be
    // settling (a popover closing, a native control giving focus up).
    find()?.focus({ preventScroll: true });
    requestAnimationFrame(() => find()?.focus({ preventScroll: true }));
}

/**
 * Keep the caret while a toolbar control is clicked.
 *
 * Cancelling the mousedown stops the focus move entirely — but only for
 * controls that do not need focus themselves: a `<select>` never opens and an
 * `<input>` never takes the caret if their mousedown is cancelled.
 */
export function keepTextEditorFocus(e) {
    if (e.target.closest('input, select, textarea')) return;
    e.preventDefault();
}

/**
 * Whether the editor is running in its phone layout.
 *
 * The chrome is dense by design, and on a phone the ribbon alone is wider than
 * the screen. Rather than leaving three toolbars to scroll sideways, the
 * components collapse: labels go, the rarely-used tools move into the "more"
 * menu, and the thumbnail strip becomes an overlay.
 */
export function useCompactLayout(query = '(max-width: 767px)') {
    // A window that reports width 0 (an embed mid-load) matches every
    // max-width query — answer "desktop" until it has a real size, or the
    // editor would fold itself away on a wide screen.
    const evaluate = () => typeof window !== 'undefined'
        && window.innerWidth > 0
        && window.matchMedia(query).matches;

    const [compact, setCompact] = useState(evaluate);
    useEffect(() => {
        const media = window.matchMedia(query);
        const update = () => setCompact(evaluate());
        update();
        media.addEventListener('change', update);
        window.addEventListener('resize', update);
        return () => {
            media.removeEventListener('change', update);
            window.removeEventListener('resize', update);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [query]);
    return compact;
}

/**
 * The hover tooltip every tool carries.
 *
 * Rendered into a PORTAL rather than inside the button. The ribbon scrolls
 * horizontally, so anything positioned inside it is clipped by that scroll box:
 * the first version of this tooltip only appeared if you scrolled the row to
 * find it, which is worse than no tooltip at all. Fixed coordinates taken from
 * the button escape the clipping entirely.
 */
function ToolTip({ anchor, label, hint }) {
    const [pos, setPos] = useState(null);

    useLayoutEffect(() => {
        const node = anchor?.current;
        if (!node) return;
        const r = node.getBoundingClientRect();
        setPos({ x: r.left + r.width / 2, y: r.bottom + 6 });
    }, [anchor]);

    if (!pos || typeof document === 'undefined') return null;
    return createPortal(
        <div
            role="tooltip"
            style={{ left: pos.x, top: pos.y, zIndex: EDITOR_Z + 50 }}
            className="pointer-events-none fixed max-w-[220px] -translate-x-1/2 rounded-md bg-[#2A2724]
                px-2.5 py-1.5 text-[12px] font-semibold leading-tight text-white shadow-lg dark:bg-zinc-700"
        >
            {label}
            {hint && hint !== label ? (
                <span className="mt-0.5 block text-[11px] font-normal leading-snug opacity-75">{hint}</span>
            ) : null}
        </div>,
        document.body,
    );
}

/** The X at the corner of a side panel's header. */
export function PanelCloseButton({ onClick }) {
    const { t } = useTranslation();
    return (
        <Hint label={t('common.close')}>
            <button
                type="button"
                onClick={onClick}
                aria-label={t('common.close')}
                className="flex h-[22px] w-[22px] flex-none items-center justify-center rounded-md text-[#A8A099] transition-colors hover:bg-[#F4F1ED] hover:text-[#3D3833] dark:hover:bg-zinc-800"
            >
                <IconX size={15} stroke={1.9} />
            </button>
        </Hint>
    );
}

export function Divider({ vertical = true }) {
    return vertical
        ? <div className="mx-1 h-8 w-px flex-none bg-[#ECE8E4] dark:bg-zinc-700" />
        : <div className="my-1 h-px w-full bg-[#ECE8E4] dark:bg-zinc-700" />;
}

/** Anchored popover rendered in a portal so page clipping never hides it. */
export function Popover({ anchorRef, open, onClose, children, align = 'start', width = 240 }) {
    const [pos, setPos] = useState(null);
    const panelRef = useRef(null);

    useLayoutEffect(() => {
        if (!open || !anchorRef.current) return undefined;
        const place = () => {
            const rect = anchorRef.current.getBoundingClientRect();
            const left = align === 'end'
                ? Math.max(8, rect.right - width)
                : Math.min(window.innerWidth - width - 8, Math.max(8, rect.left));
            setPos({ top: rect.bottom + 6, left });
        };
        place();
        window.addEventListener('resize', place);
        window.addEventListener('scroll', place, true);
        return () => {
            window.removeEventListener('resize', place);
            window.removeEventListener('scroll', place, true);
        };
    }, [open, anchorRef, align, width]);

    useEffect(() => {
        if (!open) return undefined;
        const onDown = (e) => {
            if (panelRef.current?.contains(e.target)) return;
            if (anchorRef.current?.contains(e.target)) return;
            onClose?.();
        };
        const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open, onClose, anchorRef]);

    if (!open || !pos) return null;

    return createPortal(
        <div
            ref={panelRef}
            {...{ [EDITOR_CHROME_ATTR]: '' }}
            style={{
                top: pos.top,
                left: pos.left,
                width,
                zIndex: EDITOR_Z + 50,
                // A long menu (the phone's "more" list) has to stay on screen.
                maxHeight: `calc(100vh - ${pos.top + 12}px)`,
            }}
            className="fixed overflow-y-auto rounded-xl border border-[#ECE8E4] bg-white p-3 shadow-[0_12px_32px_rgba(28,24,21,0.16)] [scrollbar-width:thin] dark:border-zinc-700 dark:bg-zinc-900"
        >
            {children}
        </div>,
        document.body,
    );
}

export function ColorGrid({ colors, value, onChange, allowTransparent = false }) {
    const { t } = useTranslation();
    return (
        <div className="flex flex-wrap gap-1.5">
            {allowTransparent ? (
                <Hint label={t('pdfEditor.options.transparent')}>
                    <button
                        type="button"
                        onClick={() => onChange('transparent')}
                        aria-label={t('pdfEditor.options.transparent')}
                        className={`relative h-6 w-6 overflow-hidden rounded-md border ${
                            value === 'transparent' ? 'border-primary ring-2 ring-primary/30' : 'border-[#ECE8E4] dark:border-zinc-700'
                        }`}
                    >
                        <span className="absolute left-1/2 top-1/2 h-[1.5px] w-8 -translate-x-1/2 -translate-y-1/2 rotate-45 bg-[#D33A2C]" />
                    </button>
                </Hint>
            ) : null}
            {colors.map((color) => (
                <Hint key={color} label={color}>
                    <button
                        type="button"
                        onClick={() => onChange(color)}
                        aria-label={color}
                        style={{ background: color }}
                        className={`h-6 w-6 rounded-md border ${
                            value?.toLowerCase() === color.toLowerCase()
                                ? 'border-primary ring-2 ring-primary/30'
                                : 'border-[#ECE8E4] dark:border-zinc-700'
                        }`}
                    />
                </Hint>
            ))}
        </div>
    );
}

export function LabeledRow({ label, children }) {
    return (
        <div className="mb-2.5 last:mb-0">
            <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-[#A8A099] dark:text-zinc-500">
                {label}
            </div>
            {children}
        </div>
    );
}

export function Slider({ value, min, max, step = 1, onChange, suffix }) {
    return (
        <div className="flex items-center gap-2">
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                className="h-1.5 flex-1 cursor-pointer appearance-none rounded-full bg-[#ECE8E4] accent-primary dark:bg-zinc-700"
            />
            <span className="w-11 text-right font-mono text-[11px] text-[#6B635C] dark:text-zinc-400">
                {value}{suffix}
            </span>
        </div>
    );
}

export function Segmented({ options, value, onChange }) {
    return (
        <div className="flex gap-1 rounded-lg bg-[#F4F1ED] p-1 dark:bg-zinc-800">
            {options.map((opt) => (
                <SegmentedOption
                    key={opt.value}
                    option={opt}
                    selected={value === opt.value}
                    onSelect={() => onChange(opt.value)}
                />
            ))}
        </div>
    );
}

/**
 * One segment.
 *
 * An icon-only segment gets the same tooltip every other tool has: the native
 * `title` used to show whatever short label the icon replaced — "L", "C", "R" —
 * which tells a first-time user nothing about what the button does.
 */
function SegmentedOption({ option, selected, onSelect }) {
    const ref = useRef(null);
    const [hovered, setHovered] = useState(false);
    const name = option.title || option.label;

    return (
        <div
            className="flex flex-1"
            onPointerEnter={() => setHovered(true)}
            onPointerLeave={() => setHovered(false)}
            onFocusCapture={() => setHovered(true)}
            onBlurCapture={() => setHovered(false)}
        >
            <button
                ref={ref}
                type="button"
                onClick={onSelect}
                aria-label={name}
                aria-pressed={selected}
                className={`flex h-7 flex-1 items-center justify-center gap-1 rounded-md px-2 text-[11.5px] font-medium transition-colors ${
                    selected
                        ? 'bg-white text-[#1C1815] shadow-sm dark:bg-zinc-900 dark:text-zinc-100'
                        : 'text-[#6B635C] hover:text-[#1C1815] dark:text-zinc-400 dark:hover:text-zinc-200'
                }`}
            >
                {option.icon ? <option.icon size={14} stroke={1.9} /> : null}
                {option.label && !option.iconOnly ? option.label : null}
            </button>
            {hovered && option.iconOnly && name
                ? <ToolTip anchor={ref} label={name} hint={option.hint} />
                : null}
        </div>
    );
}

export function MiniSelect({ value, onChange, options, width = 'w-full' }) {
    return (
        <div className={`relative ${width}`}>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="h-8 w-full appearance-none rounded-lg border border-[#ECE8E4] bg-white px-2 pr-7 text-[12.5px] text-[#1C1815] outline-none focus:border-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
            >
                {options.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        </div>
    );
}

export function MenuItem({ icon: Icon, label, onClick, active, danger }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-[12.5px] font-medium transition-colors ${
                danger
                    ? 'text-[#D33A2C] hover:bg-[#FDECEA] dark:hover:bg-red-950/30'
                    : 'text-[#3D3833] hover:bg-[#F4F1ED] dark:text-zinc-200 dark:hover:bg-zinc-800'
            }`}
        >
            {Icon ? <Icon size={16} stroke={1.8} /> : null}
            <span className="flex-1">{label}</span>
            {active ? <IconCheck size={14} stroke={2.4} className="text-primary" /> : null}
        </button>
    );
}
